'use client';
import Seo from '@repo/ui/src/components/Seo';
import { prependSlash } from '@repo/ui/src/components/Anchor/helpers';
import { SeoProps } from '@repo/ui/src/types/seo';
import { isSSR } from '@repo/ui/src/utils/isSSR';
import React, { useState } from 'react';

import MobileNavigationToggle from '@components/MobileNavigationToggle';
import { Anchor } from '@repo/ui/src/components/Anchor';
import { useLogout } from '@hooks/auth';
import { classNames } from '@repo/ui/src/utils/classNames';
import { useCustomer } from '@hooks/customer';
import { navigationClicked } from '@repo/ui/src/services/analytics';
import { LinkProps } from '@repo/ui/src/types/link';

export interface MenuLink {
  isLogout: boolean;
  link: LinkProps;
}

export interface AccountLayoutType {
  greeting?: string;
  links?: Array<MenuLink>;
}

interface Props {
  children: React.ReactNode;
  layout: AccountLayoutType;
  header?: string;
  description?: string;
  seo: SeoProps;
}

const AccountLayout = ({ children, layout, header, description, seo }: Props) => {
  const { trigger } = useLogout();
  const { data: user } = useCustomer();
  const [isOpen, toggle] = useState(false);
  const [dropdownLabel, setDropdownLabel] = useState(
    layout?.links?.[0]?.link?.displayName ?? 'Account Dashboard',
  );

  const selected = (link: MenuLink) =>
    !isSSR && location.pathname === prependSlash(link?.link?.link);

  return (
    <div className="md:px-14 lg:px-20">
      {seo && <Seo {...seo} contentRows={[]} />}
      <div className="tra-layout-aside md:grid-cols-aside sm:gap-x-14 lg:gap-x-28 max-w-body">
        <aside className="gap-y-4 bg-warm-200 after:bg-warm-200">
          <MobileNavigationToggle
            className="my-6 mx-10 bg-white py-3 px-4 border border-neutral-500 md:hidden"
            toggle={toggle}
            isOpen={isOpen}
            label={dropdownLabel}
            hideOnDesktop
            account
          />
          <div
            data-testid="account-layout-nav-wrapper"
            className={classNames(
              'absolute border border-warm-400 width-full top-6 left-10 right-10 bg-neutral-100 md:py-10 md:block md:sticky md:my-10 md:mr-14 lg:mr-28 ',
              isOpen ? 'block' : 'hidden',
            )}
          >
            <h4 className="tra-text-web-heading-4 pb-8 px-8 hidden md:block">
              {layout?.greeting ?? 'Welcome,'}&nbsp;
              {user?.firstName}
            </h4>
            <ul className="list-none pl-0">
              {layout?.links?.map((link, i) => {
                const handleClick = () => {
                  if (link?.isLogout) {
                    trigger();
                  }
                  navigationClicked({
                    nav_type: 'Account Sidebar',
                    link_url: link?.link?.link,
                    link_location: 'My Account Navigation',
                    clicked_text: link?.link?.displayName ?? '',
                    primary_header: header ?? '',
                  });
                  if (link?.link?.displayName) {
                    setDropdownLabel(link?.link?.displayName);
                  }
                  toggle(!isOpen);
                };

                const itemClassName = classNames(
                  'block outline-offset-[-2px] text-neutral-400 outline-brand-100 outline-2',
                  'focus-visible:outline cursor-pointer',
                  'hover:bg-brand-50 hover:underline hover:text-neutral-600',
                  selected(link) && 'bg-brand-50 text-neutral-600',
                );

                return (
                  <li key={i} aria-label="menu">
                    {link.isLogout ? (
                      <button
                        className={classNames(
                          itemClassName,
                          'w-full text-left bg-transparent border-0',
                        )}
                        onClick={handleClick}
                      >
                        <p className="tra-text-web-body-xl px-4 py-3 md:px-8">
                          {link?.link?.displayName}
                        </p>
                      </button>
                    ) : (
                      <Anchor
                        className={itemClassName}
                        onClick={handleClick}
                        link={link?.link?.link}
                        label={link?.link?.displayName}
                      >
                        <p className="tra-text-web-body-xl px-4 py-3 md:px-8">
                          {link?.link?.displayName}
                        </p>
                      </Anchor>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        </aside>

        <article className="px-6 py-10 md:px-0">
          <header className="mb-15 md:mb-20">
            <h2 className="tra-text-web-heading-2 mb-5" tabIndex={0}>
              {header}
            </h2>
            <hr className="border-2 w-12 border-brand-300 my-4" />
            {description && (
              <p className="tra-text-web-body-xl max-w-[370px]" tabIndex={0}>
                {description}
              </p>
            )}
          </header>
          {children}
        </article>
      </div>
    </div>
  );
};

export default AccountLayout;
